import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="month-plan-filters"
export default class extends Controller {
  static targets = [ "row", "queryInput", "departmentSelect", "clearButton", "emptyState", "groupByDept" ]

  static values = {
    debounce: Number
  }

  connect() {
    this.applyFilters()
  }

  disconnect() {
    this.clearPendingFilter()
  }

  queueNameFilter() {
    this.clearPendingFilter()
    this.filterTimeout = window.setTimeout(() => this.applyFilters(), this.debounceValue || 250)
  }

  applyDepartmentFilter() {
    this.clearPendingFilter()
    this.applyFilters()
  }

  toggleGroupByDept() {
    this.clearPendingFilter()
    this.applyFilters()
  }

  clear() {
    if (this.hasQueryInputTarget) this.queryInputTarget.value = ""
    if (this.hasDepartmentSelectTarget) this.departmentSelectTarget.value = ""

    this.clearPendingFilter()
    this.applyFilters()
    if (this.hasQueryInputTarget) this.queryInputTarget.focus()
  }

  applyFilters() {
    const nameQuery = this.queryInputTarget?.value.trim().toLowerCase() || ""
    const departmentQuery = this.departmentSelectTarget?.value.trim().toLowerCase() || ""
    const groupByDept = this.hasGroupByDeptTarget && this.groupByDeptTarget.checked

    if (groupByDept) {
      this.renderGrouped(nameQuery, departmentQuery)
    } else {
      this.renderFlat(nameQuery, departmentQuery)
    }

    const hasFilters = nameQuery.length > 0 || departmentQuery.length > 0
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.toggle("d-none", !hasFilters)
    }
  }

  renderFlat(nameQuery, departmentQuery) {
    this.removeGroupHeaders()

    const tbody = this.tableBodyElement()
    if (!tbody) return

    const rows = [...this.rowTargets].sort((a, b) =>
      parseInt(a.dataset.rowIndex || "0") - parseInt(b.dataset.rowIndex || "0")
    )
    rows.forEach(row => tbody.appendChild(row))

    let visibleCount = 0
    rows.forEach(row => {
      const visible = this.matchesFilter(row, nameQuery, departmentQuery)
      row.hidden = !visible
      if (visible) visibleCount++
    })

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("d-none", visibleCount > 0)
    }
  }

  renderGrouped(nameQuery, departmentQuery) {
    this.removeGroupHeaders()

    const tbody = this.tableBodyElement()
    if (!tbody) return

    const visibleRows = this.rowTargets.filter(row =>
      this.matchesFilter(row, nameQuery, departmentQuery)
    )

    this.rowTargets.forEach(row => { row.hidden = true })

    if (visibleRows.length === 0) {
      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.remove("d-none")
      }
      return
    }

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("d-none")
    }

    const groups = {}
    visibleRows.forEach(row => {
      const dept = row.dataset.primaryDepartment || ""
      if (!groups[dept]) groups[dept] = []
      groups[dept].push(row)
    })

    const sortedDepts = Object.keys(groups).sort((a, b) => {
      if (a === "" && b !== "") return 1
      if (a !== "" && b === "") return -1
      return a.localeCompare(b, "ru")
    })

    sortedDepts.forEach(dept => {
      const label = dept || "Без отдела"
      const headerRow = document.createElement("tr")
      headerRow.setAttribute("data-dept-header-row", "")
      headerRow.innerHTML = `<th colspan="100" class="month-plan-grid__dept-header">${this.escapeHtml(label)}</th>`
      tbody.appendChild(headerRow)

      const deptRows = groups[dept].sort((a, b) =>
        (a.dataset.sortName || "").localeCompare(b.dataset.sortName || "", "ru")
      )
      deptRows.forEach(row => {
        row.hidden = false
        tbody.appendChild(row)
      })
    })
  }

  matchesFilter(row, nameQuery, departmentQuery) {
    const filterName = row.dataset.filterName || ""
    const filterDepartments = row.dataset.filterDepartments || ""
    const deptValues = filterDepartments.length > 0 ? filterDepartments.split("|") : []

    const matchesName = nameQuery.length === 0 || filterName.includes(nameQuery)
    const matchesDept = departmentQuery.length === 0 || deptValues.includes(departmentQuery)

    return matchesName && matchesDept
  }

  removeGroupHeaders() {
    this.element.querySelectorAll("[data-dept-header-row]").forEach(el => el.remove())
  }

  tableBodyElement() {
    return this.element.querySelector("table tbody")
  }

  escapeHtml(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  clearPendingFilter() {
    if (!this.filterTimeout) return

    window.clearTimeout(this.filterTimeout)
    this.filterTimeout = null
  }
}
